import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import {
  type StepDirection,
  StepTransition,
} from "Components/Shared/Transitions/StepTransition";
import { ContentReportFormStep } from "Components/Support/ContentReport/ContentReportFormStep";
import type { ContentReportFormData } from "Components/Support/ContentReport/contentReportSchema";
import { SentryReportFormStep } from "Components/Support/SentryReportFormStep";
import { SupportFlowFooter } from "Components/Support/SupportFlowFooter";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import { SupportFlowResultStep } from "Components/Support/SupportFlowResultStep";
import type { SupportFlowStep } from "Components/Support/SupportFlowStep";
import type { SupportFormStatus } from "Components/Support/SupportFormStatus";
import type { SupportReportType } from "Components/Support/SupportReportType";
import { SupportReportTypePicker } from "Components/Support/SupportReportTypePicker";
import { SupportRequestFormStep } from "Components/Support/SupportRequestFormStep";
import type { SupportRequestFormData } from "Components/Support/supportRequestSchema";
import { useSupportRequestRole } from "Components/Support/SupportRequestCategory";
import { useFormModalClose } from "Hooks/useFormModalClose";
import { isTechnicalReportAvailable } from "Infrastructure/Utils/SupportUtils";
import {
  Resources,
  type ResourceDictionary,
  useResource,
} from "Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";
import { type ReactNode, useCallback, useState } from "react";

const RESOURCES = Resources.Support;

/** The technical report still lives under the `Resources.Sentry` namespace. */
const FORM_TITLE: Record<
  SupportReportType,
  ObjPathProxy<ResourceDictionary, string>
> = {
  technicalProblem: Resources.Sentry.FormTitle,
  supportRequest: RESOURCES.Request.Title,
  contentReport: RESOURCES.ContentReport.Title,
};

/** The picker has no target in hand, so the notice form asks for the profile's handle. */
const PICKER_CONTENT_REPORT_TARGET = { kind: "profileByHandle" } as const;

const CLEAN_FORM_STATUS: SupportFormStatus = { isDirty: false, isBusy: false };

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * State is not reset on close; SupportButton remounts this on every open instead, so the
 * closing dialog doesn't visibly snap back to the first step mid-fade.
 */
export const SupportFlowDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useResource();
  // The support line is staffed for the business queues only — merchants and hunters. Taking
  // it from the queue rather than from the access roles keeps a role added to
  // `AccessRoleCodes` failing the build in ACCESS_ROLE_TO_SUPPORT_ROLE instead of quietly
  // defaulting to no phone here. Anonymous visitors and bootstrap count as the customer queue.
  const isPhoneContactVisible = useSupportRequestRole() !== "user";

  const [step, setStep] = useState<SupportFlowStep>("picker");
  const [direction, setDirection] = useState<StepDirection>("forward");
  const [reportType, setReportType] = useState<SupportReportType | null>(null);
  const [result, setResult] = useState<SupportFlowResult | null>(null);
  const [formStatus, setFormStatus] =
    useState<SupportFormStatus>(CLEAN_FORM_STATUS);
  // One retry buffer per form: the two have different shapes and a failed send only ever
  // returns to the form it came from.
  const [retryValues, setRetryValues] = useState<
    Partial<SupportRequestFormData> | undefined
  >(undefined);
  const [contentReportRetryValues, setContentReportRetryValues] = useState<
    ContentReportFormData | undefined
  >(undefined);

  const goToStep = (
    nextStep: SupportFlowStep,
    nextDirection: StepDirection,
  ) => {
    setDirection(nextDirection);
    setStep(nextStep);
  };

  // Only the form step can be holding unsaved input. Gating on `step` rather than
  // clearing the status on the way out keeps a last render of the step that is still
  // fading away from re-arming the guard.
  const isFormStep = step === "form";
  const hasUnsavedInput = isFormStep && formStatus.isDirty;
  const isSubmitInFlight = isFormStep && formStatus.isBusy;

  const { requestNavigation, handleCloseRequest, ConfirmDialogComponent } =
    useFormModalClose({ isDirty: hasUnsavedInput, onClose });

  const handleTypeSelected = (nextReportType: SupportReportType) => {
    setReportType(nextReportType);
    setRetryValues(undefined);
    setContentReportRetryValues(undefined);
    // The form only mounts once the exit fade ends, so without clearing this the guard
    // would keep reading the previous visit's dirty flag until then.
    setFormStatus(CLEAN_FORM_STATUS);
    goToStep("form", "forward");
  };

  const handleBack = () =>
    requestNavigation(() => goToStep("picker", "backward"));

  const handleSubmitted = (
    submittedResult: SupportFlowResult,
    submittedValues?: Partial<SupportRequestFormData>,
  ) => {
    setResult(submittedResult);
    setRetryValues(submittedValues);
    goToStep("result", "forward");
  };

  const handleContentReportSubmitted = (
    submittedResult: SupportFlowResult,
    submittedValues: ContentReportFormData,
  ) => {
    setResult(submittedResult);
    setContentReportRetryValues(submittedValues);
    goToStep("result", "forward");
  };

  const handleRetry = () => {
    setResult(null);
    setFormStatus(CLEAN_FORM_STATUS);
    goToStep("form", "backward");
  };

  // Stable so the status effect in the form steps doesn't re-fire every render.
  const handleStatusChange = useCallback(
    (status: SupportFormStatus) => setFormStatus(status),
    [],
  );

  const getHeaderTitle = () => {
    if (step === "picker") {
      return t(RESOURCES.Choice.Title);
    }

    if (step === "form") {
      return reportType ? t(FORM_TITLE[reportType]) : undefined;
    }

    return undefined;
  };

  const renderStep = (currentStep: SupportFlowStep): ReactNode => {
    if (currentStep === "picker") {
      return (
        <SupportReportTypePicker
          onTypeSelected={handleTypeSelected}
          isTechnicalProblemAvailable={isTechnicalReportAvailable()}
        />
      );
    }

    if (currentStep === "form") {
      if (reportType === "technicalProblem") {
        return (
          <SentryReportFormStep
            onStatusChange={handleStatusChange}
            onSubmitted={handleSubmitted}
          />
        );
      }

      if (reportType === "contentReport") {
        return (
          <ContentReportFormStep
            target={PICKER_CONTENT_REPORT_TARGET}
            defaultValues={contentReportRetryValues}
            onStatusChange={handleStatusChange}
            onSubmitted={handleContentReportSubmitted}
          />
        );
      }

      return (
        <SupportRequestFormStep
          defaultValues={retryValues}
          onStatusChange={handleStatusChange}
          onSubmitted={handleSubmitted}
        />
      );
    }

    if (!result) {
      return null;
    }

    return <SupportFlowResultStep result={result} />;
  };

  // A step with nothing to put in the bar has to end up as `undefined` rather than as an
  // empty footer element — a JSX element is always truthy, so the dialog would draw the bar.
  const renderFooter = (): ReactNode => {
    // The picker's only footer content is the phone note.
    if (step === "picker" && !isPhoneContactVisible) {
      return undefined;
    }

    // The result speaks for itself and the header's close button dismisses it, so it earns
    // an action bar only when a retry could actually help.
    if (step === "result" && !result?.canRetry) {
      return undefined;
    }

    return (
      <SupportFlowFooter
        step={step}
        reportType={reportType}
        isSubmitInFlight={isSubmitInFlight}
        onCancel={handleCloseRequest}
        onRetry={handleRetry}
      />
    );
  };

  return (
    <>
      <ResponsiveDialog
        isOpen={isOpen}
        title={getHeaderTitle()}
        // The result step has no header title, so its name comes from here.
        accessibilityLabel={result ? t(RESOURCES.ButtonLabel) : undefined}
        onClose={handleCloseRequest}
        onBack={step === "form" ? handleBack : undefined}
        footer={renderFooter()}
      >
        <StepTransition
          stepKey={step}
          direction={direction}
          renderStep={renderStep}
        />
      </ResponsiveDialog>
      {ConfirmDialogComponent}
    </>
  );
};
